/**
 * @fileoverview Mock Financial Data Service
 * Generates realistic financial data for testing and demonstration
 */

import { 
  DEFAULT_ACCOUNT_LINES, 
  ACCOUNT_CATEGORIES, 
  calculateFinancialRatios 
} from '../models/Account';

// Entity-specific financial scenarios
const ENTITY_SCENARIOS = {
  personal: {
    name: 'Personal',
    scenario: 'individual',
    multiplier: 1,
    currency: 'USD'
  },
  small_business: {
    name: 'Tech Consulting LLC',
    scenario: 'service_business', 
    multiplier: 10,
    currency: 'USD'
  },
  rental_property: {
    name: 'Catalán Rentals SP',
    scenario: 'real_estate',
    multiplier: 25,
    currency: 'CLP'
  },
  investment_company: {
    name: 'Investment Holdings Ltd',
    scenario: 'investment',
    multiplier: 100,
    currency: 'USD'
  }
};

// Base financial values (in USD thousands) - will be scaled by entity multiplier
const BASE_FINANCIAL_DATA = {
  // ASSETS
  'cash-equivalents': {
    balance: 50, // $50k base
    previousBalance: 45,
    variance: 0.15, // ±15% random variance
    trend: 'stable'
  },
  'accounts-receivable': {
    balance: 30,
    previousBalance: 35,
    variance: 0.25,
    trend: 'decreasing'
  },
  'inventory': {
    balance: 20,
    previousBalance: 22,
    variance: 0.20,
    trend: 'decreasing'
  },
  'fixed-assets': {
    balance: 150,
    previousBalance: 155,
    variance: 0.10,
    trend: 'stable'
  },
  'investments': {
    balance: 75,
    previousBalance: 68,
    variance: 0.30,
    trend: 'increasing'
  },

  // LIABILITIES
  'accounts-payable': {
    balance: 15,
    previousBalance: 18,
    variance: 0.20,
    trend: 'decreasing'
  },
  'short-term-debt': {
    balance: 25,
    previousBalance: 30,
    variance: 0.15,
    trend: 'decreasing'
  },
  'long-term-debt': {
    balance: 80,
    previousBalance: 85,
    variance: 0.10,
    trend: 'decreasing'
  },

  // EQUITY
  'owners-equity': {
    balance: 100,
    previousBalance: 95,
    variance: 0.05,
    trend: 'increasing'
  },
  'retained-earnings': {
    balance: 75,
    previousBalance: 65,
    variance: 0.15,
    trend: 'increasing'
  },

  // INCOME (Monthly)
  'revenue': {
    balance: 40, // Monthly revenue
    previousBalance: 38,
    variance: 0.20,
    trend: 'increasing'
  },
  'other-income': {
    balance: 5,
    previousBalance: 4,
    variance: 0.30,
    trend: 'stable'
  },

  // EXPENSES (Monthly)
  'cost-of-sales': {
    balance: 20,
    previousBalance: 19,
    variance: 0.15,
    trend: 'stable'
  },
  'operating-expenses': {
    balance: 12,
    previousBalance: 13,
    variance: 0.20,
    trend: 'decreasing'
  },
  'personnel-costs': {
    balance: 8,
    previousBalance: 8,
    variance: 0.10,
    trend: 'stable'
  }
};

// Scenario-specific adjustments
const SCENARIO_ADJUSTMENTS = {
  individual: {
    'accounts-receivable': 0, // Individuals don't have AR
    'inventory': 0,
    'cost-of-sales': 0,
    'personnel-costs': 0,
    'revenue': 8, // Monthly salary/income
    'operating-expenses': 4 // Personal expenses
  },
  service_business: {
    'inventory': 0.1, // Minimal inventory
    'fixed-assets': 0.5, // Lower fixed assets
    'investments': 0.3
  },
  real_estate: {
    'accounts-receivable': 0.2, // Rent receivables
    'inventory': 0,
    'fixed-assets': 3.0, // High real estate assets
    'investments': 0.1,
    'long-term-debt': 2.0 // Mortgages
  },
  investment: {
    'accounts-receivable': 0.1,
    'inventory': 0,
    'fixed-assets': 0.2,
    'investments': 5.0, // High investment portfolio
    'revenue': 0.8, // Investment income
    'cost-of-sales': 0.1
  }
};

/**
 * Generates mock financial data for an entity
 * @param {string} entityId - Entity ID
 * @param {Object} entityConfig - Entity configuration
 * @returns {Array} Array of financial data objects
 */
export function generateMockFinancialData(entityId, entityConfig = ENTITY_SCENARIOS.personal) {
  const financialData = [];
  const asOfDate = new Date();
  const { scenario, multiplier, currency } = entityConfig;
  
  DEFAULT_ACCOUNT_LINES.forEach(accountLine => {
    const baseData = BASE_FINANCIAL_DATA[accountLine.id];
    if (!baseData) return;

    // Apply scenario adjustments
    const scenarioAdjustment = SCENARIO_ADJUSTMENTS[scenario]?.[accountLine.id] ?? 1;
    
    // Calculate variance for realistic fluctuations
    const variance = (Math.random() - 0.5) * 2 * baseData.variance;
    
    // Base balance with scenario adjustment and random variance
    let balance = baseData.balance * scenarioAdjustment * multiplier * (1 + variance);
    let previousBalance = baseData.previousBalance * scenarioAdjustment * multiplier * (1 + variance * 0.8);

    // Ensure non-negative balances for assets and positive balances for liabilities/equity
    if (accountLine.category === ACCOUNT_CATEGORIES.ASSETS) {
      balance = Math.max(0, balance);
      previousBalance = Math.max(0, previousBalance);
    }

    // Convert to proper currency amounts (thousands to actual amounts)
    balance *= 1000;
    previousBalance *= 1000;

    // Generate recent transactions
    const transactions = generateMockTransactions(accountLine, balance, 5);

    financialData.push({
      id: `${entityId}-${accountLine.id}`,
      accountLineId: accountLine.id,
      entityId,
      balance: Math.round(balance * 100) / 100, // Round to cents
      previousBalance: Math.round(previousBalance * 100) / 100,
      currency,
      asOfDate,
      transactions,
      metadata: {
        trend: baseData.trend,
        variance: variance,
        lastUpdated: asOfDate,
        dataSource: 'mock'
      }
    });
  });

  return financialData;
}

/**
 * Generates mock transactions for an account line
 * @param {Object} accountLine - Account line configuration
 * @param {number} currentBalance - Current account balance
 * @param {number} count - Number of transactions to generate
 * @returns {Array} Array of mock transactions
 */
function generateMockTransactions(accountLine, currentBalance, count = 5) {
  const transactions = [];
  const transactionTypes = getTransactionTypesForAccount(accountLine);
  
  for (let i = 0; i < count; i++) {
    const daysAgo = Math.floor(Math.random() * 30) + 1;
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    
    const transactionType = transactionTypes[Math.floor(Math.random() * transactionTypes.length)];
    const amount = generateRealisticAmount(accountLine, currentBalance);
    
    transactions.push({
      id: `txn-${accountLine.id}-${i}`,
      date,
      description: transactionType.description,
      amount,
      type: transactionType.type,
      source: transactionType.source,
      category: accountLine.category
    });
  }
  
  return transactions.sort((a, b) => b.date - a.date);
}

/**
 * Gets realistic transaction types for an account line
 * @param {Object} accountLine - Account line configuration
 * @returns {Array} Array of transaction type objects
 */
function getTransactionTypesForAccount(accountLine) {
  const transactionsByAccount = {
    'cash-equivalents': [
      { type: 'deposit', description: 'Bank transfer deposit', source: 'bank-transfer' },
      { type: 'withdrawal', description: 'ATM withdrawal', source: 'atm' },
      { type: 'payment', description: 'Online payment received', source: 'payment-processor' }
    ],
    'accounts-receivable': [
      { type: 'invoice', description: 'Invoice issued to client', source: 'invoicing-system' },
      { type: 'payment', description: 'Customer payment received', source: 'payment-processor' },
      { type: 'adjustment', description: 'Bad debt write-off', source: 'manual-adjustment' }
    ],
    'revenue': [
      { type: 'sale', description: 'Service revenue', source: 'sales-system' },
      { type: 'subscription', description: 'Monthly subscription fee', source: 'subscription-platform' },
      { type: 'consulting', description: 'Consulting project payment', source: 'project-management' }
    ],
    'operating-expenses': [
      { type: 'expense', description: 'Office supplies purchase', source: 'expense-report' },
      { type: 'subscription', description: 'Software subscription', source: 'subscription-management' },
      { type: 'utility', description: 'Utility bill payment', source: 'bill-pay' }
    ],
    'personnel-costs': [
      { type: 'salary', description: 'Monthly salary payment', source: 'payroll-system' },
      { type: 'contractor', description: 'Contractor payment', source: 'contractor-management' },
      { type: 'benefits', description: 'Health insurance premium', source: 'benefits-administration' }
    ]
  };

  return transactionsByAccount[accountLine.id] || [
    { type: 'transaction', description: `${accountLine.name} transaction`, source: 'general' }
  ];
}

/**
 * Generates realistic transaction amounts based on account type and balance
 * @param {Object} accountLine - Account line configuration
 * @param {number} currentBalance - Current account balance
 * @returns {number} Transaction amount
 */
function generateRealisticAmount(accountLine, currentBalance) {
  const balanceFactor = Math.abs(currentBalance) / 100000; // Scale based on balance size
  const baseAmount = Math.random() * 5000 * Math.max(0.1, balanceFactor);
  
  // Account-specific amount ranges
  const amountRanges = {
    'cash-equivalents': [100, 10000],
    'revenue': [1000, 50000],
    'operating-expenses': [50, 5000],
    'personnel-costs': [2000, 15000]
  };
  
  const range = amountRanges[accountLine.id] || [100, 5000];
  const amount = Math.max(range[0], Math.min(range[1], baseAmount));
  
  return Math.round(amount * 100) / 100;
}

/**
 * Gets mock financial data for multiple entities
 * @returns {Object} Financial data grouped by entity
 */
export function getAllMockFinancialData() {
  const allData = {};
  
  Object.entries(ENTITY_SCENARIOS).forEach(([entityKey, config]) => {
    const entityId = `entity-${entityKey}`;
    allData[entityId] = {
      entity: {
        id: entityId,
        name: config.name,
        currency: config.currency,
        scenario: config.scenario
      },
      financialData: generateMockFinancialData(entityId, config),
      ratios: null // Will be calculated
    };
    
    // Calculate financial ratios
    allData[entityId].ratios = calculateFinancialRatios(allData[entityId].financialData);
  });
  
  return allData;
}

/**
 * Gets formatted financial summary for an entity
 * @param {string} entityId - Entity ID
 * @param {Array} financialData - Financial data array
 * @returns {Object} Formatted financial summary
 */
export function getFinancialSummary(entityId, financialData) {
  const ratios = calculateFinancialRatios(financialData);
  const currency = financialData[0]?.currency || 'USD';
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return {
    entityId,
    currency,
    totalAssets: formatCurrency(ratios.totalAssets),
    totalLiabilities: formatCurrency(ratios.totalLiabilities),
    totalEquity: formatCurrency(ratios.totalEquity),
    monthlyIncome: formatCurrency(ratios.totalIncome),
    monthlyExpenses: formatCurrency(ratios.totalExpenses),
    netIncome: formatCurrency(ratios.netIncome),
    currentRatio: ratios.currentRatio.toFixed(2),
    returnOnAssets: (ratios.returnOnAssets * 100).toFixed(1) + '%',
    rawRatios: ratios
  };
}

// Add named export for ENTITY_SCENARIOS
export { ENTITY_SCENARIOS };

const MockFinancialDataService = {
  generateMockFinancialData,
  getAllMockFinancialData,
  getFinancialSummary,
  ENTITY_SCENARIOS
};

export default MockFinancialDataService; 